import {
  readBillingStatusField,
  readClinicalStatusField,
  readLegacyEncounterStatusField,
  readSignReqStatusField,
} from "src/app/core/helpers/encounter-clinical-status.read.util";
import { Appointment } from "src/app/core/models/admissions.model";
import { STAGE_OPTIONS } from "src/app/shared/components/stage-activity/stage-activity.component";

/** Engage account queue values — not patient pipeline (Lead / Prospect / Established). */
function isEngageQueueStageValue(raw: string): boolean {
  const v = (raw ?? "").trim().toLowerCase();
  if (!v) {
    return false;
  }
  return STAGE_OPTIONS.some(
    (o) =>
      o.value.toLowerCase() === v ||
      o.display.toLowerCase() === v ||
      o.display.toLowerCase().replace(/\s+/g, "") === v.replace(/\s+/g, "")
  );
}

/**
 * Same key precedence as the worklist grid Ref. status column (`getWorksheetRefStatusDisplay` extras)
 * (without appointment-only fallbacks like full new/est computation).
 */
const REFERRAL_LIKE_REF_STATUS_KEYS: readonly string[] = [
  "referralWorkflowStatus",
  "referral_workflow_status",
  "referralStatus",
  "referral_status",
  "engageReferralStatus",
  "lead_status",
  "leadStatus",
  "patient_status",
  "patientStatus",
  "pipelineStatus",
  "pipeline_status",
  "stage",
];

/**
 * Referral / lead pipeline labels — belong in Ref. status column and referral badges,
 * not the Enc. status / clinical schedule badge when nothing clinical exists.
 */
function isSignReqStatusLabel(raw: string): boolean {
  const compact = (raw ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "");
  return compact === "signreq";
}

/**
 * Encounter / schedule / procedure room headers: show persisted `clinicalStatus` (and schedule
 * fallbacks when clinical is empty). Omits `signReqStatus` and `billingStatus` — those are
 * separate worksheet dimensions, not clinical schedule status.
 */
export function getAppointmentHeaderClinicalStatusFromAppointment(
  appt: Appointment | null | undefined
): string {
  if (!appt) {
    return "";
  }
  const clinical = readClinicalStatusField(appt);
  if (
    clinical &&
    !isReferralPipelineEncounterDisplayLabel(clinical) &&
    !isSignReqStatusLabel(clinical)
  ) {
    return clinical;
  }
  const fromSched = (appt.encounterSchedule?.scheduleStatus || "")
    .toString()
    .trim();
  if (
    fromSched &&
    !isReferralPipelineEncounterDisplayLabel(fromSched) &&
    !isSignReqStatusLabel(fromSched)
  ) {
    return fromSched;
  }
  const topSched = (
    (appt as Appointment & { scheduleStatus?: string }).scheduleStatus || ""
  )
    .toString()
    .trim();
  if (
    topSched &&
    !isReferralPipelineEncounterDisplayLabel(topSched) &&
    !isSignReqStatusLabel(topSched)
  ) {
    return topSched;
  }
  const legacy = readLegacyEncounterStatusField(appt);
  if (
    legacy &&
    !isReferralPipelineEncounterDisplayLabel(legacy) &&
    !isSignReqStatusLabel(legacy)
  ) {
    return legacy;
  }
  return "";
}

/**
 * Worksheet / admissions DOS badge / Enc. status column:
 * `clinicalStatus` (or nested encounter_status row), then legacy `encounterStatus`,
 * then persisted billing/sign-req dimensions, then schedule row `scheduleStatus`.
 */
export function getWorksheetStatusRawFromAppointment(
  appt: Appointment | null | undefined
): string {
  if (!appt) {
    return "";
  }
  const signReq = readSignReqStatusField(appt);
  if (signReq && !isReferralPipelineEncounterDisplayLabel(signReq)) {
    return signReq;
  }
  const billing = readBillingStatusField(appt);
  if (billing && !isReferralPipelineEncounterDisplayLabel(billing)) {
    return billing;
  }
  const clinical = readClinicalStatusField(appt);
  if (clinical && !isReferralPipelineEncounterDisplayLabel(clinical)) {
    return clinical;
  }
  const encounterStatus = readLegacyEncounterStatusField(appt);
  if (
    encounterStatus &&
    !isReferralPipelineEncounterDisplayLabel(encounterStatus)
  ) {
    return encounterStatus;
  }
  const fromSched = (appt.encounterSchedule?.scheduleStatus || "").toString().trim();
  if (fromSched && !isReferralPipelineEncounterDisplayLabel(fromSched)) {
    return fromSched;
  }
  const topSched = (
    (appt as Appointment & { scheduleStatus?: string }).scheduleStatus || ""
  )
    .toString()
    .trim();
  if (topSched && !isReferralPipelineEncounterDisplayLabel(topSched)) {
    return topSched;
  }
  return "";
}

export function isReferralPipelineEncounterDisplayLabel(enc: string): boolean {
  const compact = (enc ?? "").toLowerCase().replace(/[\s_-]+/g, "");
  return (
    compact === "prospect" ||
    compact === "lead" ||
    compact === "newlead" ||
    compact === "newreferral" ||
    compact === "warm" ||
    compact === "cold"
  );
}

/**
 * Derive the Ref. status / patient-status label from an Engage referral row or any plain object
 * using the same rules as the worklist grid column so headers stay consistent.
 */
export function worksheetRefStatusDisplayFromReferralLikeRow(
  row: Record<string, unknown> | null | undefined
): string {
  if (!row || typeof row !== "object") {
    return "--";
  }
  const fromModel = String(row["referralWorkflowStatus"] ?? "").trim();
  if (fromModel) {
    return formatWorksheetPatientStatusDisplay(fromModel);
  }
  for (const k of REFERRAL_LIKE_REF_STATUS_KEYS) {
    const v = row[k];
    if (v != null && String(v).trim() !== "") {
      const s = String(v).trim();
      if (k === "stage" && isEngageQueueStageValue(s)) {
        continue;
      }
      return formatWorksheetPatientStatusDisplay(s);
    }
  }
  const enc = String(row["encounterStatus"] ?? row["encounter_status"] ?? "").trim();
  if (enc) {
    const compact = enc.toLowerCase().replace(/[\s_-]+/g, "");
    if (
      compact === "prospect" ||
      compact === "lead" ||
      compact === "newlead" ||
      compact === "newreferral" ||
      compact === "warm" ||
      compact === "cold"
    ) {
      return formatWorksheetPatientStatusDisplay(enc);
    }
  }
  const neo = String(
    row["newOrEstablishedStatus"] ??
      row["new_or_established_status"] ??
      row["NewOrEstablishedStatus"] ??
      ""
  ).trim();
  if (neo) {
    return formatWorksheetPatientStatusDisplay(neo);
  }
  return "--";
}

/** Patient Status column pill colors — mirrors worklist grid. */
export function worksheetPatientStatusBadgeNgClassFromLabel(
  label: string
): Record<string, boolean> {
  const trimmed = (label ?? "").trim();
  if (!trimmed || trimmed === "--") {
    return {};
  }
  const n = trimmed
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s_-]+/g, "");
  if (n === "newpatient" || n === "newreferral") {
    return { "badge-soft-info": true };
  }
  if (n === "established") {
    return { "badge-soft-success": true };
  }
  return { "badge-soft-secondary": true };
}

/**
 * Worklist / worksheet patient-status column: consistent labels for Engage / list-clinic values.
 */
/** Title-case each word for patient-status badges (e.g. `established` → `Established`). */
function titleCasePatientStatusWords(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function formatWorksheetPatientStatusDisplay(raw: string): string {
  const s = (raw ?? "").trim();
  if (!s || s === "--") {
    return "--";
  }
  const compact = s.toLowerCase().replace(/[\s_-]+/g, "");
  if (compact === "newlead" || compact === "lead") {
    return "Lead";
  }
  if (compact === "prospect") {
    return "Prospect";
  }
  if (compact === "established") {
    return "Established";
  }
  if (compact === "newpatient" || compact === "newreferral") {
    return "New Patient";
  }
  if (/[\s_-]/.test(s)) {
    return titleCasePatientStatusWords(s.replace(/[_-]+/g, " "));
  }
  return titleCasePatientStatusWords(s);
}
